// Dutch (je) copy for /preview-first-uploads, translated from copy.en.ts. Same shape (checked by copy.test.ts).
// The second pass is always "de originelen" (never "volledige kwaliteit"). The uploader's own labels are in
// English, so they're quoted as it shows them.
import type { PreviewFirstCopy } from "./copy.en";

const nl: PreviewFirstCopy = {
  seo: {
    title: "Eerst previews, bij alle abonnementen | Photohawk",
    description:
      "Nieuw in de web-uploader van Photohawk, bij alle abonnementen: van elke foto gaat eerst een kleine preview omhoog, zodat je galerij binnen minuten online kan, kan verkopen en gedeeld kan worden, terwijl de originelen op de achtergrond volgen.",
  },
  hero: {
    badge: "Nieuw",
    eyebrow: "In de web-uploader · Alle abonnementen",
    title: "Je galerij binnen minuten online,",
    titleHighlight: "niet pas na uren",
    subtitle:
      "De web-uploader stuurt nu eerst een kleine preview van elke foto. Je galerij kan met die previews al online, zodat kopers hun foto's kunnen vinden, kopen en delen terwijl de originelen op de achtergrond verder uploaden.",
    ctaPrimary: "Open de uploader",
    ctaSecondary: "Zo werkt het",
    uploaderLink: "Bekijk alles wat nieuw is in de web-uploader",
  },
  steps: {
    heading: "Zo werkt het",
    subtitle: "Eerst de previews zit in de web-uploader, bij alle abonnementen. Als je foto's toevoegt, zie je hoe lang de upload op elke manier duurt en kies je “Previews first, originals later”.",
    stepLabel: "Stap {n}",
    items: [
      {
        id: "add",
        title: "Voeg je foto's toe",
        body: "Sleep foto's of een hele map erin, zoals je gewend bent. Je browser maakt van elke foto een kleine preview, gewoon op je eigen computer.",
      },
      {
        id: "previews",
        title: "Eerst gaan de previews omhoog",
        body: "De previews zijn klein, dus ze zijn snel geüpload, zelfs met een trage verbinding op locatie.",
      },
      {
        id: "live",
        title: "Je galerij gaat online",
        body: "Publiceer zodra de previews erop staan, of vink “Publish the gallery as soon as the previews are up” aan en het gebeurt vanzelf. Kopers kunnen meteen bladeren, zoeken, kopen en delen.",
      },
      {
        id: "full",
        title: "De originelen volgen",
        body: "De originelen uploaden op de achtergrond verder, in dezelfde foto's. Downloads en afdrukken gebruiken altijd de originelen.",
      },
    ],
    timeline: {
      label: "Een voorbeeldupload, van begin tot eind",
      previews: "Previews",
      previewsTime: "minuten",
      live: "Galerij online",
      full: "Originelen",
      fullTime: "op de achtergrond",
    },
  },
  buyers: {
    heading: "Wat kopers zien",
    subtitle: "Niets nieuws te leren. Kopers krijgen de galerij die ze kennen, alleen eerder.",
    items: [
      {
        id: "protected",
        title: "Met watermerk en beschermd, zoals altijd",
        body: "Kopers bekijken dezelfde previews met watermerk als altijd, en previewbescherming werkt precies hetzelfde als je galerij die gebruikt.",
      },
      {
        id: "search",
        title: "Ze vinden zichzelf meteen",
        body: "Zoeken op selfie en op startnummer werkt met de previews, dus iedereen vindt zijn foto's zodra de galerij online staat.",
      },
      {
        id: "delivery",
        title: "Originelen, automatisch geleverd",
        body: "Een aankoop of een gratis download wordt nooit als preview geleverd. Koopt of downloadt iemand een foto voordat het origineel binnen is, dan gaat het origineel voor in de wachtrij en krijgt die persoon het zodra het klaar is.",
      },
    ],
  },
  strip: {
    heading: "Houd het in de gaten terwijl je uploadt",
    body: "Terwijl previews en originelen uploaden, laat een balk bovenaan de uploader zien hoe je galerij ervoor staat: of hij al online is, hoeveel originelen er nog moeten komen en wanneer een gekochte foto naar voren is gegaan. Zodra de galerij online is, zie je ook hoeveel mensen hem de afgelopen 30 minuten hebben bezocht, en eigenaren en beheerders van de werkruimte zien de nieuwe bestellingen sinds de upload begon.",
    exampleLabel: "Voorbeeld",
    live: "Online met previews",
    orders: "3 bestellingen sinds je begon",
    visitors: "42 bezoekers in de afgelopen 30 min",
    left: "Nog 1.250 originelen te gaan",
    jumped: "Originelen van 2 bestelde foto's naar voren gehaald",
  },
  faq: {
    heading: "Vragen",
    items: [
      {
        q: "Moet ik de uploader open laten?",
        a: "Dat is het beste, tot de originelen klaar zijn; de balk laat zien hoeveel er nog over zijn. Sluit je hem eerder, dan gaat er niets verloren. Je galerij blijft online met de previews, en de volgende keer dat je die galerij in de uploader opent, zie je hoeveel foto's er nog wachten. Voeg dezelfde map opnieuw toe (ook later, vanuit huis) en alleen de ontbrekende originelen worden verstuurd.",
      },
      {
        q: "Wat als iemand een foto koopt voordat het origineel er is?",
        a: "Kopen kan gewoon. Zolang de uploader draait, gaat het origineel van die foto voor in de wachtrij. De bestelmail wacht op het bestand en gaat binnen een paar minuten nadat het binnen is de deur uit, zodat de koper nooit per ongeluk de preview krijgt. Was de uploader gesloten, dan wacht de bestelling tot je de map opnieuw toevoegt. De uploader laat zien op welke foto's een koper wacht, en die gaan eerst.",
      },
      {
        q: "Werkt het ook voor galerijen met gratis downloads?",
        a: "Ja. Iedereen kan bekijken, zoeken en downloaden zodra de previews erop staan, en een download is nooit de preview. Downloadt iemand een foto voordat het origineel binnen is, dan gaat het origineel voor in de wachtrij: de download laat zien dat het origineel nog wordt geüpload en wordt een werkende download zodra het bestand binnen is, en een download per e-mail gaat dan ook de deur uit. Elke download telt één keer.",
      },
      {
        q: "Werkt zoeken op selfie en startnummer met de previews?",
        a: "Ja. Gezichten en startnummers worden op de previews herkend, dus zoeken werkt zodra ze erop staan. Zolang de uploader open is, stuurt hij ook scherpere close-ups uit je originelen mee om het zoeken te helpen.",
      },
      {
        q: "Bij welke abonnementen zit het?",
        a: "Alle abonnementen, ook Lite. Heeft je werkruimte eerder al met eerst de previews geüpload, dan worden die foto's gewoon afgemaakt: voeg dezelfde map opnieuw toe en hun originelen worden verstuurd, en hun bestellingen en downloads komen nog steeds aan.",
      },
      {
        q: "Kost het iets extra?",
        a: "Nee. Het zit bij alle abonnementen inbegrepen, en elke foto telt één keer: de preview en het origineel zijn dezelfde foto. Het enige verschil is dat je verbinding in totaal iets meer data verstuurt (ongeveer 5% meer).",
      },
      {
        q: "Werkt het met FTP en Photohawk Studio?",
        a: "Op dit moment niet. Eerst de previews is onderdeel van de web-uploader. Foto's die via FTP of vanuit Photohawk Studio worden verstuurd, gaan zoals voorheen in één keer als originelen omhoog.",
      },
      {
        q: "Staat het standaard aan?",
        a: "Nee. Elke keer dat je foto's toevoegt, laat de web-uploader zien hoe lang ze duren met en zonder eerst de previews, en de originelen meteen versturen is geselecteerd. Kies “Previews first, originals later” als je de galerij eerder online wilt; de web-uploader onthoudt je keuze voor de volgende keer. Met een lagere uploadresolutie (“Large” of “Web”) worden alleen verkleinde bestanden geüpload, dus dan geldt eerst de previews niet.",
      },
    ],
  },
  cta: {
    heading: "Probeer het bij je volgende upload",
    body: "Eerst de previews zit in de web-uploader, bij alle abonnementen. Voeg je foto's toe, kies eerst de previews en je galerij kan binnen minuten online staan.",
    button: "Open de uploader",
  },
};

export default nl;
